import type { ICitation } from 'src/types/citation';
import type { IImage, IImageForUpload } from 'src/types/image';

import { mutate } from 'swr';
import { z as zod } from 'zod';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import { Box, Stack, Alert, Button, Dialog, Typography, DialogContent } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useImageUrlToFile } from 'src/hooks/use-convert-image-url-to-file';

import { fetcher, getEndpoints } from 'src/utils/axios';
import {
  getImageUrl,
  getImageThumbnail,
  AMUSE_MAX_IMAGE_SIZE,
  AMUSE_SUPPORTED_IMAGE_FORMATS,
} from 'src/utils/file-service';

import { useAddCitation } from 'src/actions/citations';
import { useCreateUserProfileImage } from 'src/actions/user';
import { useMediaUpdates } from 'src/contexts/media-updates-context';
import { useCreateOrganizationProfileImage } from 'src/actions/organization';
import { useUpdateImage, useDeleteImage, useCreateImageForStory } from 'src/actions/image';

import { Image } from 'src/components/image';
import { Form, RHFTextField, schemaHelper } from 'src/components/hook-form';

import { IProcStatus } from 'src/types/generic';
import { ContentFlagContentType } from 'src/types/content-flag';

import { Iconify } from '../iconify';
import { Upload } from '../upload/upload';
import { ImageCrop } from '../image-crop';
import { ButtonEdit } from '../button-edit';
import { UploadBox } from '../upload/upload-box';
import { ImageUploadDialog } from './image-upload-dialog';
import { CitationsList } from '../citations/citations-list';
import { DEFAULT_THUMBNAIL_AREA } from '../image-crop/types';
import { NewContentFlagDialog } from '../content-flag/new-content-flag-dialog';

type ImageSchemaType = zod.infer<typeof ImageSchema>;
const ImageSchema = zod.object({
  image: schemaHelper.file({ message: { required_error: 'Image is required.' } }),
  thumbnailArea: zod
    .object({
      x1: zod.number().min(0).max(1),
      y1: zod.number().min(0).max(1),
      x2: zod.number().min(0).max(1),
      y2: zod.number().min(0).max(1),
    })
    .optional(),
  altText: zod
    .string()
    .max(1024, {
      message: 'Alt text must be fewer than 1024 characters.',
    })
    .optional(),
});

type Props = {
  image?: IImage;
  cropAspect?: number;
  addFirstImage?: {
    execute: (imageForUpload: IImageForUpload) => Promise<{ guid: string }>;
    loading: boolean;
  };
  imageForMediaType: string;
  onImageUpdated?: (imageGuid: string) => void;
  remove?: {
    execute: () => Promise<void>;
    loading: boolean;
  };
  imageCitationsButton?: boolean;
  allowEdit?: boolean;
  storyGuid?: string;
  placeGuid?: string;
  showExistingPlaceImages?: boolean;
  showImageSearch?: boolean;
  useUploadDialog?: boolean;
  useContentFlagDialog?: boolean;
  onContentFlagDialogClose?: () => void;
  userGuid?: string;
  organizationGuid?: string;
  procStatus?: IProcStatus;
};

export function ImageViewReplace({
  image,
  cropAspect,
  imageForMediaType,
  onImageUpdated,
  remove,
  imageCitationsButton = false,
  allowEdit = true,
  storyGuid,
  userGuid,
  organizationGuid,
  addFirstImage,
  placeGuid,
  showExistingPlaceImages = false,
  showImageSearch = false,
  useUploadDialog = false,
  useContentFlagDialog = false,
  onContentFlagDialogClose,
  procStatus,
}: Props) {
  const replaceMode = useBoolean();
  const [imageUploadDialogOpen, setImageUploadDialogOpen] = useState(false);
  const [imageUploadFile, setImageUploadFile] = useState<File | undefined>(undefined);
  const [imageCitationsDialogOpen, setImageCitationsDialogOpen] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const { replaceImage, isReplacing } = useUpdateImage();
  const { deleteImage, isDeleting } = useDeleteImage();
  const { createImageForStory, isCreating } = useCreateImageForStory(storyGuid!);
  const { createUserProfileImage } = useCreateUserProfileImage(userGuid!);
  const { createOrganizationProfileImage } = useCreateOrganizationProfileImage(organizationGuid!);
  const { convertImageUrlToFile } = useImageUrlToFile();
  const { addCitation: addImageCitation } = useAddCitation('image');
  const [contentFlagDialogOpen, setContentFlagDialogOpen] = useState(false);
  const lastCreatedImageGuidRef = useRef<string | null>(null);
  const submitSucceededRef = useRef(false);
  const [sseProcCompleted, setSseProcCompleted] = useState(false);

  const { subscribe } = useMediaUpdates();

  useEffect(() => {
    setSseProcCompleted(false);
  }, [image?.guid, image?.s3Guid]);

  useEffect(() => {
    if (procStatus === IProcStatus.Completed) {
      setSseProcCompleted(false);
    }
  }, [procStatus]);

  useEffect(() => subscribe((update) => {
      if (update.type === 'image' && update.state === 'completed' && (update.guid === image?.guid || update.s3Guid === image?.s3Guid)) {
        setImageLoadError(false);
        setSseProcCompleted(true);
        onImageUpdated?.(image?.guid || update.guid || '');
      }
    }), [subscribe, onImageUpdated, image?.guid, image?.s3Guid]);

  const imageUrl = useMemo(() => {
    if (image) {
      return getImageUrl(image);
    }
    return undefined;
  }, [image]);

  const effectiveProcStatus = useMemo(() => {
    if (procStatus === IProcStatus.Failed) {
      return IProcStatus.Failed;
    }
    if (sseProcCompleted || procStatus === IProcStatus.Completed) {
      return IProcStatus.Completed;
    }
    if (procStatus !== undefined && procStatus !== null) {
      return procStatus;
    }
    return IProcStatus.Processing;
  }, [sseProcCompleted, procStatus]);

  const [imageFile, setImageFile] = useState<File | undefined>(undefined);

  const processingDoneForUrl = effectiveProcStatus === IProcStatus.Completed;

  useEffect(() => {
    if (imageUrl) {
      convertImageUrlToFile(imageUrl).then((file) => {
        setImageFile(file || undefined);
      });
    } else {
      setImageFile(undefined);
    }
    // Re-fetch when processing finishes: URL is unchanged but assets become available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, processingDoneForUrl]);

  const cropDisplaySource = useMemo(() => {
    if (!replaceMode.value) {
      return undefined;
    }
    // Prefer CDN URL in the cropper: a File from fetch() can be empty/invalid while the URL still loads in <img> (CORS/cache timing after processing).
    if (image && imageUrl) {
      return imageUrl;
    }
    return imageFile ?? imageUrl;
  }, [replaceMode.value, image, imageUrl, imageFile]);

  // If there's no image, we're already in "add" mode
  const [isAddMode, setIsAddMode] = useState(!image);

  useEffect(() => {
    setIsAddMode(!image);
  }, [image]);

  const handleDeleteImage = useCallback(async () => {
    try {
      await deleteImage(image?.guid || '');
      toast.success('Image deleted successfully');
      onImageUpdated?.(image?.guid || '');
    } catch (error) {
      console.error(error);
    }
  }, [deleteImage, image?.guid, onImageUpdated]);

  const defaultValues = {
    image: image ? getImageUrl(image) : undefined,
    thumbnailArea: undefined,
    altText: '',
  };

  const methods = useForm<ImageSchemaType>({
    mode: 'all',
    resolver: zodResolver(ImageSchema),
    defaultValues,
  });

  const { handleSubmit, watch, reset } = methods;

  useEffect(() => {
    if (!image) {
      return;
    }
    // SWR revalidation updates `image` by reference while the user is in replace mode; resetting here
    // clears dirty state and blanks the cropper until refresh.
    if (replaceMode.value) {
      return;
    }
    reset({
      image: getImageUrl(image),
      thumbnailArea: undefined,
      altText: image?.alt || '',
    });
    setImageLoadError(false);
  }, [image, reset, replaceMode.value]);

  const onSubmit = handleSubmit(async (data) => {
    submitSucceededRef.current = false;
    try {
      const imageForUpload: IImageForUpload = {
        file: data.image as File,
        thumbnailX1: data.thumbnailArea?.x1 ?? 0,
        thumbnailY1: data.thumbnailArea?.y1 ?? 0,
        thumbnailX2: data.thumbnailArea?.x2 ?? 1,
        thumbnailY2: data.thumbnailArea?.y2 ?? 1,
        alt: data.altText ?? '',
      };

      if (!image && storyGuid) {
        const result = await createImageForStory(imageForUpload);
        lastCreatedImageGuidRef.current = result.guid;
        submitSucceededRef.current = true;
        toast.success('Image added successfully');
        replaceMode.onFalse();
        setIsAddMode(false);
        reset();
        onImageUpdated?.(result.guid);
        onAtlasStoryChanged();
        if (addFirstImage) {
          await addFirstImage.execute(imageForUpload);
        }
        return;
      }

      if (!image && userGuid) {
        const result = await createUserProfileImage(imageForUpload);
        lastCreatedImageGuidRef.current = result.guid;
        submitSucceededRef.current = true;
        toast.success('Image added successfully');
        replaceMode.onFalse();
        setIsAddMode(false);
        reset();
        onImageUpdated?.(result.guid);
        return;
      }

      if (!image && organizationGuid) {
        const result = await createOrganizationProfileImage(imageForUpload);
        lastCreatedImageGuidRef.current = result.guid;
        submitSucceededRef.current = true;
        toast.success('Image added successfully');
        replaceMode.onFalse();
        setIsAddMode(false);
        reset();
        onImageUpdated?.(result.guid);
        return;
      }

      if (!image && addFirstImage) {
        const result = await addFirstImage.execute(imageForUpload);
        lastCreatedImageGuidRef.current = result.guid;
        submitSucceededRef.current = true;
        toast.success('Image added successfully');
        replaceMode.onFalse();
        setIsAddMode(false);
        reset();
        onImageUpdated?.(result.guid);
        return;
      }

      const rawImage = data.image;
      const resolvedFile =
        rawImage instanceof File
          ? rawImage
          : typeof rawImage === 'string' && rawImage
            ? await convertImageUrlToFile(rawImage)
            : image
              ? await convertImageUrlToFile(getImageUrl(image))
              : null;

      if (!resolvedFile) {
        toast.error('Could not load image for upload. Try again or refresh the page.');
        return;
      }

      const imageForUploadResolved: IImageForUpload = {
        file: resolvedFile,
        thumbnailX1: data.thumbnailArea?.x1 ?? 0,
        thumbnailY1: data.thumbnailArea?.y1 ?? 0,
        thumbnailX2: data.thumbnailArea?.x2 ?? 1,
        thumbnailY2: data.thumbnailArea?.y2 ?? 1,
        alt: data.altText ?? '',
      };

      // Use the hook to replace the image
      const result = await replaceImage(imageForUploadResolved, image!, imageForMediaType);
      submitSucceededRef.current = true;
      onAtlasStoryChanged();

      // Notify parent if needed
      if (onImageUpdated) {
        onImageUpdated(result);
      }

      if (rawImage instanceof File) {
        toast.success('Image replaced successfully');
      } else {
        toast.success('Image updated successfully');
      }

      replaceMode.onFalse();
      reset();
    } catch (error) {
      console.error(error);
    }
  });

  const imageReq = watch('image');

  const textForProcStatus = useMemo(() => {
    switch (effectiveProcStatus) {
      case IProcStatus.Pending:
        return 'Image is queued for processing';
      case IProcStatus.Processing:
        return 'Image is processing';
      case IProcStatus.Completed:
        return 'Image processed successfully';
      case IProcStatus.Failed:
        return 'Image failed to process';
      default:
        return 'Image is processing';
    }
  }, [effectiveProcStatus]);

  const isLoading = isReplacing || isCreating || (addFirstImage?.loading ?? false);

  const onAtlasStoryChanged = useCallback(() => {
    if (useContentFlagDialog) {
      setContentFlagDialogOpen(true);
    }
  }, [useContentFlagDialog]);

  const imageCitationsDialog = (
    <Dialog
      open={imageCitationsDialogOpen}
      onClose={() => setImageCitationsDialogOpen(false)}
      fullWidth
      maxWidth="md"
    >
      <DialogContent
        sx={{
          p: 3,
        }}
      >
        <CitationsList
          title="Image Sources"
          type="image"
          guid={image ? image.guid : ''}
          icon={
            <Image
              alt={image?.alt}
              src={getImageUrl(image)}
              sx={{ width: 48, height: 48, borderRadius: 1 }}
            />
          }
          minHeight="512px"
        />
      </DialogContent>
    </Dialog>
  );

  // Add image form - used when no image exists yet or in replace mode
  const renderAddOrReplaceImage = (
    <Form methods={methods} onSubmit={onSubmit}>
      {useUploadDialog && (
        <ImageUploadDialog
          open={imageUploadDialogOpen}
          showExistingPlaceImages={showExistingPlaceImages}
          showImageSearch={showImageSearch}
          placeGUID={placeGuid!}
          imageFile={imageUploadFile}
          onClose={() => setImageUploadDialogOpen(false)}
          cropAspect={cropAspect}
          onUpload={async (newImage, altText, thumbnailArea, imageURL, sourcePlaceImageGuid) => {
            methods.setValue('image', newImage);
            methods.setValue('altText', altText);
            methods.setValue('thumbnailArea', thumbnailArea);
            submitSucceededRef.current = false;
            await onSubmit();
            if (!submitSucceededRef.current) return;
            const citationImageGuid = image?.guid || lastCreatedImageGuidRef.current || '';
            lastCreatedImageGuidRef.current = null;
            if (citationImageGuid) {
              if (imageURL) {
                await addImageCitation(
                  { details: 'Image source', url: imageURL },
                  citationImageGuid
                );
              }
              if (sourcePlaceImageGuid) {
                const citationsResponse = await fetcher(getEndpoints.image.citations(sourcePlaceImageGuid)) as { data: ICitation[] };
                const sourceCitations = citationsResponse?.data ?? [];
                await Promise.all(
                  sourceCitations.map((c) =>
                    addImageCitation({ details: c.details, url: c.url, body: c.body }, citationImageGuid)
                  )
                );
              }
              if (imageURL || sourcePlaceImageGuid) {
                await mutate(getEndpoints.image.citations(citationImageGuid), undefined, {
                  revalidate: true,
                });
              }
            }
          }}
        />
      )}
      <Stack spacing={1}>
        {!isAddMode && (
          <Stack direction="row" justifyContent="flex-end">
            {remove && (
              <LoadingButton
                type="button"
                variant="outlined"
                sx={{ mr: 'auto' }}
                loading={remove.loading}
                onClick={async () => {
                  try {
                    await remove.execute();
                    replaceMode.onFalse();
                    reset();
                    toast.success('Image removed successfully');
                  } catch (error) {
                    console.error(error);
                  }
                }}
                disabled={isLoading}
                color="error"
              >
                Remove
              </LoadingButton>
            )}
            <LoadingButton
              type="button"
              variant="outlined"
              onClick={() => {
                replaceMode.onFalse();
                reset();
              }}
              disabled={isLoading}
            >
              Cancel
            </LoadingButton>
            <LoadingButton
              type="submit"
              variant="contained"
              disabled={!methods.formState.isDirty}
              loading={isLoading}
              color="primary"
              sx={{ ml: 2 }}
            >
              Save
            </LoadingButton>
          </Stack>
        )}
        {!(imageReq instanceof File) && image && !replaceMode.value && imageLoadError && (
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              minHeight: 200,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.neutral',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: 'center',
                p: 2,
              }}
            >
              {textForProcStatus}
            </Typography>
          </Box>
        )}

        {useUploadDialog && !replaceMode.value && (
          <Upload
            maxSize={AMUSE_MAX_IMAGE_SIZE}
            accept={AMUSE_SUPPORTED_IMAGE_FORMATS}
            multiple={false}
            disabled={isLoading}
            type="image"
            onDrop={(acceptedFiles) => {
              setImageUploadFile(acceptedFiles[0]);
              setImageUploadDialogOpen(true);
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setImageUploadDialogOpen(true);
            }}
          />
        )}

        {replaceMode.value && (
          <>
            {cropDisplaySource && cropAspect && (
              <ImageCrop
                image={cropDisplaySource}
                aspect={cropAspect}
                onCropComplete={(thumbnailArea) => {
                  const formImage = imageFile ?? imageUrl;
                  if (formImage) {
                    methods.setValue('image', formImage, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                  methods.setValue('thumbnailArea', thumbnailArea, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                initialCroppedArea={methods.watch('thumbnailArea') ?? DEFAULT_THUMBNAIL_AREA}
                disabled={isLoading}
              />
            )}
            <Stack direction="row" spacing={1}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Replace</Typography>
                <UploadBox
                  disabled={isLoading}
                  maxSize={AMUSE_MAX_IMAGE_SIZE}
                  accept={AMUSE_SUPPORTED_IMAGE_FORMATS}
                  multiple={false}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setImageUploadDialogOpen(true);
                  }}
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Alt text</Typography>
                <RHFTextField
                  name="altText"
                  placeholder="Enter alt text"
                  fullWidth
                  disabled={isLoading}
                />
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </Form>
  );

  // View mode - used when an image exists and we're not in replace mode
  const renderViewImage = (
    <>
      <Stack spacing={1} sx={{ position: 'relative' }}>
        {effectiveProcStatus !== IProcStatus.Completed || imageLoadError ? (
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: effectiveProcStatus === IProcStatus.Failed ? 'error.lighter' : 'background.neutral',
            }}
          >
            <Stack spacing={1}>
              <Alert
                severity={effectiveProcStatus === IProcStatus.Failed ? 'error' : 'info'}
                sx={{ width: '100%', p: 2 }}
              >
                {textForProcStatus}
              </Alert>
              {effectiveProcStatus === IProcStatus.Failed && (
                <Stack direction="row" justifyContent="center">
                  <LoadingButton
                    variant="contained"
                    color="error"
                    onClick={handleDeleteImage}
                    loading={isDeleting}
                  >
                    Delete image
                  </LoadingButton>
                </Stack>
              )}
            </Stack>
          </Box>
        ) : (
          <>
            <Image
              alt={image?.alt || ''}
              key={`${image?.s3Guid}-${effectiveProcStatus}`}
              src={getImageThumbnail(image?.s3Guid || '')}
              sx={{ borderRadius: 2 }}
              onError={() => setImageLoadError(true)}
            />
            {allowEdit && (
              <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <Stack direction="row" justifyContent="flex-end">
                  <ButtonEdit onClick={replaceMode.onTrue} />
                </Stack>
              </Box>
            )}
          </>
        )}
        <Typography variant="caption">{image?.alt}</Typography>
        {imageCitationsButton ? (
          <Box marginTop={1} marginBottom={1}>
            <Button
              startIcon={<Iconify icon="mdi:book-open-variant" />}
              variant="contained"
              onClick={() => setImageCitationsDialogOpen(true)}
            >
              Image Sources
            </Button>
          </Box>
        ) : null}
      </Stack>
      {imageCitationsDialog}
    </>
  );

  return (
    <>
      {isAddMode || replaceMode.value ? renderAddOrReplaceImage : (
        <>
          {useContentFlagDialog && (
            <NewContentFlagDialog
              contentType={ContentFlagContentType.IMAGE}
              guid={image?.guid || ''}
              open={contentFlagDialogOpen}
              isRespondingToChange
              onClose={() => {
                setContentFlagDialogOpen(false);
                onContentFlagDialogClose?.();
              }}
            />
          )}
          {renderViewImage}
        </>
      )}
    </>
  );
}
